import React, { useEffect, useState, useContext, useRef } from "react";
import InfinityGridLabelBrandsConnections from "../../common/cardContainers/InfinityGridLabelBrandsConnections";
import { APP_TITLE } from "../../../config/constant";
import { useOutletContext } from "react-router";
import ListComponent from "../../common/lists/ListComponent";
import { useSelector } from "react-redux";
import * as Library from "../../../utils/Library";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import { formatDefaultDateTime } from "../../../utils/Formatter";
import { AppQueryClient } from "../../../api/queryClient";
import styled from "styled-components";
import DialogBoxComponent from "../../common/dialogs/DialogBoxComponent";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import { defaultStatusColors } from "../../../utils/CardUtils";
import MainWindowHeader, { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import AppContext from "../../../AppContext";
import { useQueryClient } from "react-query";
import DialogCancelReasonsComponent from "../../common/dialogs/DialogCancelReasonsComponent";
import { roleAccesse } from "../../../utils/roleManager";
import LogDetailDialogBox from "../../common/dialogs/LogDetailDialogBox";

// #region  SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: hidden;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }
`;
// #endregion

export default function LabelBrandsConnectionsPage() {
    document.title = `${APP_TITLE} - Label Brands Connections`;

    // #region VARS
    const [isLoading, setIsLoading] = useState(false);
    const [showInvitationAccepted, setShowInvitationAccepted] = useState(false);
    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");
    const { MainLayoutProps, layoutType } = useOutletContext();
    const navigations = useSelector(state => state.navigationAccesses);
    const queryClient = useQueryClient();
    const gridRef = useRef(null);

    const [listNumRows, setListNumRows] = useState(50);
    const { pageRefreshFromNotification, setPageRefreshFromNotification, userData } = useContext(AppContext);

    const userRole = userData?.role;
    const editAccess = roleAccesse(userRole, "retail brand account admin");

    //---------- Header Filters
    const [searchText, setSearchText] = useState("");
    const [labelBrandID, setLabelBrandID] = useState(null);
    const [retailBrandID, setRetailBrandID] = useState(null);
    //-----
    const [allLabelBrandsList, setAllLabelBrandsList] = useState([]);
    const [filteredLabelBrand, setFilteredLabelBrand] = useState([]);
    //-----
    const [allStatusList, setAllStatusList] = useState([
        { name: "Connected", value: "Connected", selected: true },
        { name: "Disconnected", value: "Disconnected", selected: true }
    ]);
    const [filteredStatus, setFilteredStatus] = useState([]);
    //-----
    const [requestEnabled, setRequestsEnabled] = useState(false);

    const filters = {
        labelBrandIDs: filteredLabelBrand?.map(item => item?.value),
        statuses: filteredStatus?.map(item => item?.value),
        searchText: searchText
    };

    const infiniteGridQuerykey = ["LabelBrandsConnectionsGrid", filters];
    const listQueryKey = ["LabelBrandsConnectionsList", filters];

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const [connectedBrandID, setConnectedBrandID] = useState(null);
    const [labelBrandName, setLabelBrandName] = useState("");
    const [retailBrandName, setRetailBrandName] = useState("");
    const [showDisconnectingLoader, setShowDisconnectingLoader] = useState(false);
    const [showResconnectingLoader, setShowResconnectingLoader] = useState(false);
    const [reasonObj, setReasonObj] = useState({
        reason1: false,
        reason2: false,
        reason3: false,
        reason4: false,
        otherReasonText: ""
    });

    const [logData, setLogData] = useState([]);
    const [isOpenLogModalBox, setIsOpenLogModalBox] = useState(false);
    const [logLabelBrandName, setLogLabelBrandName] = useState("");
    const [logRetailBrandName, setLogRetailBrandName] = useState("");
    // #endregion

    // #region LIST COMPONENT PROPS
    const listColumns = [
        {
            name: "Brand",
            selector: row => row?.labelBrand?.name ?? "--"
        },
        {
            name: "Connected to",
            selector: row => row?.retailBrand?.name ?? "--"
        },
        {
            name: "Invited by",
            selector: row => row?.invite?.invitedByFullName ?? "--"
        },
        {
            name: "BOTAG ID",
            selector: row => row?.ourID ?? "--"
        },
        {
            name: "Last connected on",
            selector: row => formatDefaultDateTime(row?.lastConnectedOn) ?? "--"
        },
        {
            name: "Last disconnected on",
            selector: row => formatDefaultDateTime(row?.lastDisconnectedOn) ?? "--"
        },
        {
            name: "Max stores allowed",
            selector: row => row?.maxStores ?? "--"
        },
        {
            name: "Active stores",
            selector: row => row?.retailerActiveStores ?? "--"
        }
    ];
    // #endregion

    // #region METHODS
    const onLabelBrandChange = (selections, dropdownDataWithSelections) => {
        setFilteredLabelBrand(selections);
        setAllLabelBrandsList(dropdownDataWithSelections);
    };

    const onStatusChange = (selections, dropdownDataWithSelections) => {
        setFilteredStatus(selections);
        setAllStatusList(dropdownDataWithSelections);
    };

    const onFiltersLoaded = data => {
        if (data?.connectedLabelBrands?.length) {
            const labelBrands = data?.connectedLabelBrands?.map(item => {
                const defaultLabelBrandID = localStorage.getItem("defaultLabelBrand");
                let labelBrandName = item?.name;

                if (item?._id === defaultLabelBrandID) {
                    labelBrandName = `${item?.name} (default)`;
                }

                return {
                    name: labelBrandName,
                    value: item?._id,
                    selected: true
                };
            });

            setAllLabelBrandsList(labelBrands);
            setFilteredLabelBrand(labelBrands);
        }

        setRequestsEnabled(true);
    };

    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") AppQueryClient.invalidateQueries(listQueryKey);
        else {
            //else AppQueryClient.invalidateQueries(infiniteGridQuerykey);
            gridRef.current?.reset?.(infiniteGridQuerykey);
        }
    };

    const handleAcceptInvitation = async inviteID => {
        const res = await Library.makeCommonPostRequest(
            "Invites",
            "acceptRetailerInvite",
            { inviteID: inviteID },
            false,
            setIsLoading
        );

        if (res?.data?.status === 200) {
            /* Library.showToastMessage({
                type: "success",
                title: "Invitation accepted!",
                message: "You can now proceed with affiliating your stores to this brand."
            }); */
            setShowInvitationAccepted(true);
        }
        onClickRefreshAction();
    };

    const handleDisconnect = async (connectedBrandID, disconnectReason) => {
        setConnectedBrandID(null);
        const res = await Library.makeCommonPostRequest(
            "Global",
            "disconnectBrand",
            {
                connectedBrandID: connectedBrandID,
                disconnectReason: disconnectReason,
                labelBrandID: labelBrandID || "",
                retailBrandID: retailBrandID || ""
            },
            false,
            setShowDisconnectingLoader
        );

        if (res?.data?.status === 200) {
            Library.showToastMessage({
                type: "success",
                title: "Label Brand disconnected!",
                message: `${labelBrandName} is now disconnected from ${retailBrandName}.`
            });

            onClickRefreshAction();
            onCloseDisconnectDialog();
        }
    };

    const onCloseDisconnectDialog = () => {
        setLabelBrandID(null);
        setRetailBrandID(null);
        setConnectedBrandID(null);
        setLabelBrandName("");
        setRetailBrandName("");
        setReasonObj({
            reason1: false,
            reason2: false,
            reason3: false,
            reason4: false,
            otherReasonText: ""
        });
    };

    const handleReconnect = async data => {
        const res = await Library.makeCommonPostRequest(
            "Global",
            "reconnectBrand",
            {
                connectedBrandID: data?._id,
                labelBrandID: data?.labelBrand?._id || "",
                retailBrandID: data?.retailBrand?._id || ""
            },
            false,
            setShowResconnectingLoader
        );
        if (res?.data?.status === 200) {
            Library.showToastMessage({
                type: "success",
                title: "Retailer Reconnected",
                message: `You’ve successfully reconnected ${data?.labelBrand?.name} to your brand.`
            });
            onClickRefreshAction();
        }
    };

    const onDisconnect = data => {
        setLabelBrandID(data?.labelBrand?._id || "");
        setConnectedBrandID(data?._id);
        setLabelBrandName(data?.labelBrand?.name || "");
        setRetailBrandName(data?.retailBrand?.name || "");
        setRetailBrandID(data?.retailBrand?._id || ""); //Check if this is the coorect value
    };

    const onSearch = searchText => {
        setSearchText(searchText);
    };

    const onCloseLogModalBox = () => {
        setIsOpenLogModalBox(false);
    };

    const onStatusLog = data => {
        setIsOpenLogModalBox(true);
        setLogData(data?.statusLog);
        setLogLabelBrandName(data?.labelBrand?.name || "");
        setLogRetailBrandName(data?.retailBrand?.name || "");
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);

        setupHeaders(
            props,
            onClickRefreshAction,
            navigations,
            allStatusList,
            allLabelBrandsList,
            onLabelBrandChange,
            onStatusChange,
            onFiltersLoaded,
            requestEnabled,
            filteredStatus,
            onSearch,
            editAccess,
            userRole
        );
    }, [
        props?.layoutType,
        requestEnabled,
        allLabelBrandsList,
        filteredLabelBrand,
        allStatusList,
        filteredStatus,
        requestEnabled,
        editAccess,
        userRole
    ]);

    useEffect(() => {
        Library.removeQueries(queryClient, infiniteGridQuerykey);
        Library.removeQueries(queryClient, listQueryKey);
    }, []);

    useEffect(() => {
        if (pageRefreshFromNotification) {
            setPageRefreshFromNotification(false);
            onClickRefreshAction();
        }
    }, [pageRefreshFromNotification]);
    // #endregion

    // #region RENDER
    return (
        <StyledPage>
            <FullScreenLoader isLoading={isLoading} />

            <FullScreenOverlay show={isOpenLogModalBox}>
                <LogDetailDialogBox
                    log={logData}
                    onClose={onCloseLogModalBox}
                    logLabelBrandName={logLabelBrandName}
                    logRetailBrandName={logRetailBrandName}
                />
            </FullScreenOverlay>

            <FullScreenOverlay show={showDisconnectingLoader || showResconnectingLoader}>
                <DialogBoxComponent
                    headingColor={showResconnectingLoader ? "#00BB00" : "#FF3636"}
                    heading={showResconnectingLoader ? "RECONNECTING..." : "DISCONNECTING..."}
                    showCancel={false}
                    showConfirm={false}
                    loading={showDisconnectingLoader || showResconnectingLoader}
                />
            </FullScreenOverlay>

            <DialogCancelReasonsComponent
                visible={connectedBrandID !== null}
                onCancelClicked={() => {
                    onCloseDisconnectDialog();
                }}
                onConfirmClicked={reason => {
                    let declineReason = reason?.reasonTitle;
                    if (reason?.isOtherReason) {
                        declineReason = reason?.otherReasonText;
                    }
                    handleDisconnect(connectedBrandID, declineReason);
                }}
                reasonsArray={[
                    "I’m no longer working with them",
                    "This will be temporary",
                    "I’ll stop working with this label brand"
                ]}
                reasonObj={reasonObj}
                setReasonObj={setReasonObj}
                title="Disconnect from label brand?"
                message="You are about to cancel the connection with this label brand. You'll no longer be able to share data nor check data with this brand. All fulfillment centers will be SUSPENDED."
                confirmText="CONFIRM"
                cancelText="CANCEL"
                heading="ATTENTION"
                reasonText="Please share the main reason to disconnect."
                headingColor="var(--text-icons-orange-500)"
                subReasonText="The reason you select will be shared with the label brand."
            />

            <FullScreenOverlay show={showInvitationAccepted}>
                <DialogBoxComponent
                    title="Invitation accepted!"
                    message="You can now proceed with affiliating your stores to this brand."
                    showCancel={false}
                    confirmText="CONTINUE"
                    heading="SUCCESS!"
                    headingColor="var(--text-icons-green-500)"
                    onClickYes={() => {
                        setShowInvitationAccepted(false);
                        //navigate("/ffcenters/management");
                    }}
                />
            </FullScreenOverlay>

            {viewLayoutStyle === "Grid" && (
                <InfinityGridLabelBrandsConnections
                    ref={gridRef}
                    //minColumnWidth="370px"
                    minColumnWidth="238px"
                    limit="16"
                    queryKey={infiniteGridQuerykey}
                    // statusFromPath="status"
                    onAcceptInvitation={handleAcceptInvitation}
                    filters={filters}
                    requestEnabled={requestEnabled}
                    onDisconnect={onDisconnect}
                    handleReconnect={handleReconnect}
                    onStatusLog={onStatusLog}
                />
            )}
            {viewLayoutStyle === "List" && (
                <ListComponent
                    title="BRANDS"
                    //title2="(LAST 10 INVITED)"
                    columns={listColumns}
                    api={{
                        endpoint: "getConnectedLabelBrands",
                        formData: {
                            companyID: localStorage.getItem("currentCompanyID"),
                            page: 1,
                            limit: listNumRows,
                            filters: filters
                        }
                        //onSuccess: mapListData
                    }}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    actions={{
                        includeActions: true,
                        editIsVisible: false,
                        suspendIsVisible: false,
                        removeIsVisible: false,
                        viewIsVisible: false,
                        extraActions: [
                            {
                                title: row => (row?.status === "Disconnected" ? "ARCHIVE" : "UNLINK"),
                                onClick: row => {
                                    if (row?.status === "Inactive" || row?.status === "Disconnected") return undefined;
                                    onDisconnect(row);
                                },
                                isDisabled: row => {
                                    return row?.status === "Inactive" || row?.status === "Disconnected";
                                }
                            },
                            {
                                title: row => (row.status === "Disconnected" ? "RECONNECT" : "MORE STORES"),
                                onClick: row => {
                                    if (row.status === "Disconnected") {
                                        handleReconnect(row);
                                    } else {
                                        return undefined;
                                    }
                                },
                                isDisabled: row => {
                                    return row.status !== "Disconnected";
                                }
                            }
                        ]
                    }}
                    includeStatus={{
                        stickyStatus: true,
                        statusFromPath: "status",
                        statusColors: defaultStatusColors
                    }}
                    requestEnabled={requestEnabled}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                />
            )}
        </StyledPage>
    );
    // #endregion
}

// #region FUNCTIONS
function setupHeaders(
    props,
    onClickRefreshAction,
    navigations,
    allStatusList,
    allLabelBrandsList,
    onLabelBrandChange,
    onStatusChange,
    onFiltersLoaded,
    requestEnabled,
    filteredStatus,
    onSearch,
    editAccess,
    userRole
) {
    const createMode = navigations?.navigationAccesses?.labelbrands?.createModeOnly;

    props.setControlBarLeftBtns([
        { customSearch: <CustomSearch enabled={true} onTextChange={onSearch} placeholder="Search" /> }
    ]);
    props.setControlBarCenterBtns([
        { to: "/labelbrands/connections", active: true, disabled: createMode, title: "CONNECTIONS" },
        { to: "/labelbrands/invitations", active: false, disabled: false, title: "INVITATIONS" },
        { to: "/labelbrands/requests", active: false, disabled: false, title: "REQUESTS" }
    ]);
    props.setControlBarRightBtns([
        { to: "#", active: true, disabled: createMode, title: "View", icon: "eye" },
        {
            to: editAccess ? "/labelbrands/request" : "#",
            active: false,
            disabled: !editAccess,
            title: "Request",
            icon: "mail"
        },
        { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: true
                }}
                onRefreshClick={onClickRefreshAction}
                firstOrganize={{
                    visible: true,
                    dropdownData: allLabelBrandsList,
                    onDropdownChange: onLabelBrandChange,
                    disabled: allLabelBrandsList?.length === 1,
                    type: allLabelBrandsList?.length === 1 ? "singleSelect" : "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all label brands",
                        labelAllSelected: "All label brands",
                        labelNoneSelected: "--",
                        allItems: allLabelBrandsList
                    }
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allStatusList,
                    onDropdownChange: onStatusChange,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all status",
                        labelAllSelected: "All status",
                        labelNoneSelected: "--",
                        allItems: allStatusList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        useConnectedLabelBrands: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
            />
        )
    });
}
// #endregion
